/**
 * Serviço de grupos para Supabase
 * Implementação simples sem dependência de tipos TypeScript
 */

import { supabase } from '@/integrations/supabase/client';

export interface Taxa {
  id: number;
  tenant_id: number;
  grupo_id?: number;
  nome: string;
  valor?: number;
  percentual?: number;
  tipo: string;
  descricao?: string;
  created_at: string;
}

export interface Grupo {
  id: number;
  tenant_id: number;
  numero_grupo: number;
  especie: string | null;
  duracao_total_meses: number | null;
  meses_restantes: number | null;
  meses_restantes_com_adendos: number | null;
  maximo_participantes?: number | null;
  tipo_plano?: string | null;
  indice_reajuste?: string | null;
  proxima_assembleia_n?: number | null;
  data_proxima_assembleia?: string | null;
  data_vencimento_grupo?: string | null;
  data_limite_envio?: string | null;
  data_constituicao?: string | null;
  dia_assembleia?: number | null;
  dia_vencimento?: number | null;
  dia_limite_envio?: number | null;
  created_at: string;
  updated_at: string;
  titulo?: string | null;
  empresa?: string | null;
  tipo_contrato?: string | null;
  regra_lances?: string | null;
  // Novos campos de taxas
  tx_adm_total?: string | null;
  tx_adm_parcelas?: string | null;
  adiantamento_tx_adm?: string | null;
  fundo_reserva?: string | null;
  seguro_vida_mensal?: string | null;
  // Campo de controle
  ativo?: boolean | null;
  observacoes?: string | null;
  taxas?: Taxa[];
  bens?: any[];
}

export interface CreateGrupoDto {
  tenant_id: number;
  numero_grupo: number;
  especie: string;
  duracao_total_meses: number;
  meses_restantes: number;
  meses_restantes_com_adendos: number;
  maximo_participantes?: number;
  tipo_plano?: string;
  indice_reajuste?: string;
  proxima_assembleia_n?: number;
  data_proxima_assembleia?: string;
  data_vencimento_grupo?: string;
  data_limite_envio?: string;
  data_constituicao?: string;
  dia_assembleia?: number;
  dia_vencimento?: number;
  dia_limite_envio?: number;
  titulo?: string;
  empresa?: string;
  tipo_contrato?: string;
  regra_lances?: string;
  // Novos campos de taxas
  tx_adm_total?: string;
  tx_adm_parcelas?: string;
  adiantamento_tx_adm?: string;
  fundo_reserva?: string;
  seguro_vida_mensal?: string;
  ativo?: boolean;
  observacoes?: string;
}

export interface UpdateGrupoDto extends Partial<CreateGrupoDto> {}

export class GruposSupabaseService {
  async findAll(): Promise<Grupo[]> {
    const { data, error } = await supabase
      .from('grupos')
      .select('*, bens(*)')
      .order('numero_grupo', { ascending: true });

    if (error) throw error;
    return data || [];
  }

  async findById(id: string | number): Promise<Grupo | null> {
    const { data, error } = await supabase
      .from('grupos')
      .select('*, bens(*)')
      .eq('id', typeof id === 'string' ? Number(id) : id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data;
  }

  async create(data: CreateGrupoDto): Promise<Grupo> {
    const { data: result, error } = await supabase
      .from('grupos')
      .insert(data)
      .select()
      .single();

    if (error) throw error;
    return result;
  }

  async update(id: string | number, data: UpdateGrupoDto): Promise<Grupo> {
    const { data: result, error } = await supabase
      .from('grupos')
      .update(data)
      .eq('id', typeof id === 'string' ? Number(id) : id)
      .select()
      .single();

    if (error) throw error;
    return result;
  }

  async delete(id: string | number): Promise<void> {
    const { error } = await supabase
      .from('grupos')
      .delete()
      .eq('id', typeof id === 'string' ? Number(id) : id);

    if (error) throw error;
  }

  // Métodos específicos para grupos
  async findByNumero(numero: number | string): Promise<Grupo | null> {
    const { data, error } = await supabase
      .from('grupos')
      .select('*')
      .eq('numero_grupo', typeof numero === 'string' ? Number(numero) : numero)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data;
  }

  async findByEspecie(especie: string): Promise<Grupo[]> {
    const { data, error } = await supabase
      .from('grupos')
      .select('*')
      .eq('especie', especie)
      .order('numero_grupo', { ascending: true });

    if (error) throw error;
    return data || [];
  }

  async findByTenantId(tenantId: string | number): Promise<Grupo[]> {
    const { data, error } = await supabase
      .from('grupos')
      .select('*')
      .eq('tenant_id', typeof tenantId === 'string' ? Number(tenantId) : tenantId)
      .order('numero_grupo', { ascending: true });

    if (error) throw error;
    return data || [];
  }
}

// Instância singleton
export const gruposSupabaseService = new GruposSupabaseService();


